import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { type NotificationsContextApi } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { getErrorResponseMessage } from "@via/utils/helpers.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  AppDeployOutcome,
  AppListResponse,
  CallAppRequestBody,
  CallReportAppRequestBody,
  RedeployApplicationResponseBody,
  UpdateApplicationRequestBody,
} from "common";

/**
 * A save answers whether the deploy after it worked (#953). `deployed: false` means the change is
 * saved but the apps cluster did not take it: a warning, not a failure.
 */
const notifySaveOutcome = (
  data: unknown,
  saved: { id: string; message: string; details: string },
  setNotification: NotificationsContextApi["setNotification"],
  NOTIFICATION: { ALERT: number; SUCCESS: number }
): void => {
  const outcome = data as Partial<AppDeployOutcome> | null | undefined;
  if (outcome && typeof outcome === "object" && outcome.deployed === false) {
    setNotification({
      id: `${saved.id}-not-deployed`,
      message: "Saved, but not deployed",
      details:
        outcome.message ??
        "The apps cluster could not take this change. Retry the deploy from My Apps.",
      variant: NOTIFICATION.ALERT,
    });
    return;
  }
  setNotification({ ...saved, variant: NOTIFICATION.SUCCESS });
};

export const fetchAppsList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter: Array<{ id: string; value: unknown }>
): Promise<AppListResponse[]> => {
  try {
    const baseUrl = "/api/app/v1";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });
    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    if (filter && filter.length > 0) {
      filter.map(f => {
        queryParams.append(f.id, f.value as string);
      });
    }
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useAppsList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
): any => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/app/v1", take, skip, sort, filter],
    queryFn: () => fetchAppsList(take, skip, sort, filter),
    retry: false,
    enabled,
  });
};

export const fetchAppsInstanceList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>
): Promise<AppListResponse> => {
  try {
    const baseUrl = "/api/app/v1/instances";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });
    if (sort && sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useAppsInstanceList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  { refetchInterval }
): any => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery({
    queryKey: ["/api/app/v1/instances", take, skip, sort],
    queryFn: () => fetchAppsInstanceList(take, skip, sort),
    retry: true,
    refetchOnMount: "always",
    refetchInterval,
  });
};

export const useLaunchApplication = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (body: {
      id: number;
      payload: CallReportAppRequestBody | CallAppRequestBody;
    }) => {
      const res = await axios.post(`/api/app/v1/call/${body.id}`, body.payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "app-launching",
        message: "Application Launching",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the inout file list
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1"] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1/instances"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "app-launch-failed",
        message: "Launching Application Failed",
        details: error.response?.data?.message || "Please check the logs",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useTerminateAppInstance = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.get(`/api/app/v1/terminate/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "app-terminated",
        message: "App Successfully Terminated",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the inout file list
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1/instances"] });
    },
    onError: error => {
      setNotification({
        id: "app-terminate-failed",
        message: "Terminate Application Failed",
        details:
          "If you encounter this issue again, please contact your system administrator",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteApp = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await axios.delete(`/api/app/v1/${id}`);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "app-deleted",
        message: "Application Successfully Deleted",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the inout file list
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1"] });
    },
    onError: error => {
      setNotification({
        id: "app-delete-failed",
        message: "Delete Application Failed",
        details: "Deleted Apps can not be recovered",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateApp = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async payload => {
      const res = await axios.post("/api/app/v1", payload);
      return res.data;
    },
    onSuccess: data => {
      notifySaveOutcome(
        data,
        {
          id: "app-created",
          message: "App Successfully Created",
          details: "You can now launch this app",
        },
        setNotification,
        NOTIFICATION
      );
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1"] });
      return data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "app-creation-failed",
        message: "App Creation Failed",
        details: getErrorResponseMessage(error) ?? "App could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateApp = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      payload: UpdateApplicationRequestBody;
    }) => {
      const res = await axios.post(`/api/app/v1/${params.id}`, params.payload);
      return res.data;
    },
    onSuccess: data => {
      notifySaveOutcome(
        data,
        {
          id: "app-updated",
          message: "App Successfully Updated",
          details: "You can now launch this app",
        },
        setNotification,
        NOTIFICATION
      );
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1"] });
      return data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "app-update-failed",
        message: "App Update Failed",
        details: getErrorResponseMessage(error) ?? "App could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateAppRevision = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (params: {
      id: number;
      payload: UpdateApplicationRequestBody;
    }) => {
      const res = await axios.post(
        `/api/app/v1/revision/${params.id}`,
        params.payload
      );
      return res.data;
    },
    onSuccess: data => {
      notifySaveOutcome(
        data,
        {
          id: "app-revision-created",
          message: "App Revision Successfully Updated",
          details: "You can now launch this app",
        },
        setNotification,
        NOTIFICATION
      );
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1"] });
      return data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "app-revision-update-failed",
        message: "App Revision Update Failed",
        details: getErrorResponseMessage(error) ?? "App could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

/** Retry deploy: deploys the app's latest saved definition again (owner or admin). */
export const useRedeployApp = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    RedeployApplicationResponseBody,
    ErrorNotification,
    number
  >({
    mutationFn: async (id: number) => {
      const res = await axios.post(`/api/app/v1/${id}/deploy`);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "app-redeployed",
        message: "Deploy started",
        details: "The app can be launched in about a minute.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "app-redeploy-failed",
        message: "App Deploy Failed",
        details:
          getErrorResponseMessage(error) ??
          "The apps cluster could not take the deploy. Try again later.",
        variant: NOTIFICATION.ERROR,
      });
      // The row is not_deployed now whatever it was before, so show that.
      queryClient.invalidateQueries({ queryKey: ["/api/app/v1"] });
    },
  });
};
